import fetch from 'isomorphic-fetch';
import { encode } from 'html-entities';

import { redirectToUrl } from '@gumtree/ui-library/src/utils/browser-service';
import { getNewInstance as getNewG5SInstance } from '@gumtree/ui-library/src/utils/suggestions-service-g5s';
import {
    ISuggestionsService,
    KeywordSuggestionsResponse,
} from '@gumtree/ui-library/src/utils/suggestions-service-interface';
import { GrowthBookFeature } from '@gumtree/shared/src/model/experiment';
import { trackGA4PreNav } from '@gumtree/shared/src/util/ga4-shared';
import { normalizeLocationType } from '@gumtree/shared/src/util/location-utils';

import { RecentSearchesItem, save as saveSearches } from './recent-service/recent-searches-service';
import { save as saveLocations } from './recent-service/recent-locations-service';
import { G5SParams } from './reducers/g5s-params';
import { getGrowthbookClientSide } from '../../growthbook/init-growthbook';

let internalSuggestionsService: ISuggestionsService | undefined;
let isSubmitSearchCalled = false; // Browser seems to have a bug that calls submitSearch() a second time after a click on search item. This ensures submitSearch() is only ever called once

export const getSuggestionsService = () => internalSuggestionsService;

export const setIsSubmitSearchCalled = (val) => (isSubmitSearchCalled = val);

export const setupSuggestionsService = (g5SParams: G5SParams) => {
    internalSuggestionsService = getNewG5SInstance(
        g5SParams.busId,
        g5SParams.timeStamp,
        g5SParams.sign
    );
};

export const fetchLocationSuggestions = (
    value: string
): Promise<{ name: string; type: string; id?: string }[]> =>
    fetch(`/ajax/location/prefix?input=${value}`)
        .then((response) => response.json())
        .then((response) => response.locationList as { name: string; type: string; id?: string }[]);

export type KeywordSuggestion = {
    name: string;
    highlight: string;
    category?: string;
    categoryDisplayName?: string;
    searchOptionsExactMatch?: boolean;
};

export const transformResponseToSuggestions = (
    results: KeywordSuggestionsResponse
): KeywordSuggestion[] => {
    const getHighlight = (suggestion: KeywordSuggestionsResponse[number]) => {
        return suggestion?._highlightResult?.completion?.matchedWords?.[0] || '';
    };

    const [first, ...rest] = results;
    const withoutCategory =
        getGrowthbookClientSide()?.getFeatureValue(GrowthBookFeature.SUG_WITHOUT_CATEGORY, 'A') ===
        'B';
    return [
        ...(first ? [{ name: first.completion, highlight: getHighlight(first) }] : []),
        ...(!withoutCategory && first?.categories
            ? first.categories.map(({ id, canonicalName, localizedNames }) => {
                if (shouldShowCategory(id)) {
                    return {
                        name: first.completion,
                        highlight: getHighlight(first),
                        category: canonicalName,
                        categoryDisplayName: localizedNames?.en_GB,
                    }
                }
                return {
                    name: first.completion,
                    highlight: getHighlight(first),
                }
            })
            : []),
        ...rest.map((suggestion) => {
            const data: KeywordSuggestion = {
                name: suggestion.completion,
                highlight: getHighlight(suggestion),
            };
            if (withoutCategory) {
                data.searchOptionsExactMatch = true;
                if (shouldShowCategory(suggestion.categories[0].id)) {
                    data.category = suggestion.categories[0].canonicalName;
                    data.categoryDisplayName = suggestion.categories[0].localizedNames?.en_GB;
                }
            }
            return data;
        }),
    ];
};

export const shouldShowCategory = (categoryId: string) => {
    return categoryId !== '1';
}

export const fetchKeywordSuggestions = async (value: string): Promise<KeywordSuggestion[]> => {
    const suggestionsService = getSuggestionsService();

    return !suggestionsService
        ? Promise.resolve([])
        : suggestionsService
              .search(value)
              .then((results: KeywordSuggestionsResponse = []) =>
                  transformResponseToSuggestions(results)
              );
};

export const submitSearch = ({
    category = 'all',
    categoryDisplayName,
    keyword,
    location,
    locationDetails,
    searchOptionsExactMatch,
    additionalParams = {},
    requireKeywordCorrection,
    keywordPopulatedBy,
}: {
    category: string | undefined;
    categoryDisplayName: string | undefined;
    keyword: string;
    location: string;
    locationDetails?: { name: string; type?: string; id?: string };
    searchOptionsExactMatch: boolean;
    additionalParams?: Record<string, string | boolean | number>;
    requireKeywordCorrection: boolean;
    keywordPopulatedBy: GA4.SubmitListingSearchEvent['search']['keywordPopulatedBy'];
}) => {
    if (isSubmitSearchCalled) {
        return;
    }

    isSubmitSearchCalled = true;

    const queryParams: string[] = [];

    if (category) {
        queryParams.push(`search_category=${category}`);
    }

    if (keyword) {
        queryParams.push(`q=${keyword}`);
        queryParams.push(`search_term_populated_by=${keywordPopulatedBy}`);

        if (requireKeywordCorrection) {
            queryParams.push(`keyword_correction=suggest`);
        }

        const recentSearch: RecentSearchesItem = {
            name: keyword,
        };

        if (category) {
            recentSearch.category = category;
        }

        if (categoryDisplayName) {
            recentSearch.categoryDisplayName = categoryDisplayName;
        }

        if (searchOptionsExactMatch) {
            queryParams.push(`searchOptionsExactMatch=true`);
            recentSearch.searchOptionsExactMatch = true;
        }

        saveSearches(recentSearch);
    }

    if (location) {
        queryParams.push(`search_location=${encode(location)}`);

        const isSrp4B = getGrowthbookClientSide()?.getFeatureValue(GrowthBookFeature.SRP_4, 'A') === 'B';
        if (isSrp4B) {
            trackGA4PreNav<GA4.SelectLocationClick>({
                event: 'select_location_click',
                locationType: normalizeLocationType(locationDetails?.type?.toUpperCase()),
                locationName: location,
                locationId: locationDetails?.id,
            });
        }

        // Save location with only name, type, and id (strip any extra fields)
        const cleanLocation = locationDetails 
            ? { 
                name: locationDetails.name, 
                ...(locationDetails.type && { type: locationDetails.type }),
                ...(locationDetails.id && { id: locationDetails.id })
              }
            : { name: location };
        saveLocations(cleanLocation);
    }

    const paramsKeys = Object.keys(additionalParams);

    if (paramsKeys.length) {
        paramsKeys.forEach((param) => {
            queryParams.push(`${param}=${additionalParams[param]}`);
        });
    }

    const searchUrl = `/search?${queryParams.join('&')}`;

    redirectToUrl(searchUrl);
};
